import { find, has, map, uniq } from 'lodash';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';

import accountsResource from 'src/actions/accounts/accountsResource';
import channelsConfigurationResource from 'src/actions/channels/channelsConfigurationResource';
import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import customChannelResource from 'src/actions/channels/customChannelResource';
import endpointsResource, { Endpoint } from 'src/actions/endpoints/endpointsResource';
import { EndpointTypes } from 'src/models/Channels/endpointType';
import { EndpointsSections } from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointConstants';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import { toastError } from 'src/actions/toast';
import useCustomChannelsConfiguration from 'src/presentation/components/pages/shared/hooks/useCustomChannelsConfiguration';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useInboxes from './useInboxes';
import useResource, { getConsolidatedState, someFailed, STATUS_FAILED } from 'src/presentation/hooks/useResource';

export default function useEntryPointTypes() {
  const dispatch = useDispatch();

  const { isFeatureEnabled: isCustomChannelsEnabled } = useCustomChannelsConfiguration();
  const { values: customChannels = [], loadingState: customChannelsLoadingState } = useResource(customChannelResource, {
    fetch: isCustomChannelsEnabled,
  });

  const { values: endpoints, loadingState: endpointsLoadingState } = useResource<Endpoint>(endpointsResource);
  const { loadingState: inboxesStateLoadingState } = useResource(routingGroupsResource);
  const { value: channelsConfiguration, loadingState: channelsConfigurationLoadingState } =
    useDocumentResource(channelsConfigurationResource);

  const { loadingState: communicationQueuesLoadingState, entryPointsErrors: errors } =
    useResource(communicationQueuesResource);
  const { loadingState: accountsLoadingState } = useResource(accountsResource);
  const { loadingState: ivrsLoadingState } = useResource(ivrsResource);

  const { inboxesMap } = useInboxes();

  const loadingStates = [
    accountsLoadingState,
    channelsConfigurationLoadingState,
    communicationQueuesLoadingState,
    endpointsLoadingState,
    inboxesStateLoadingState,
    ivrsLoadingState,
    ...(isCustomChannelsEnabled ? [customChannelsLoadingState] : []),
  ];

  const fetchError = someFailed(...loadingStates);
  const loadingState = fetchError ? STATUS_FAILED : getConsolidatedState(loadingStates);

  const availableChannelTypes = useMemo(() => {
    const activeEndpoints = getActiveEndpoints(endpoints, customChannels, isCustomChannelsEnabled);

    const channels = getChannelTypesWithConfiguration(channelsConfiguration, customChannels, activeEndpoints);

    return map(channels, channel => ({
      ...channel,
      inbox: inboxesMap[channel.defaultInbox],
      slaMinutes: (channel.defaultSLASeconds || 0) / 60,
    }));
  }, [channelsConfiguration, customChannels, endpoints, inboxesMap, isCustomChannelsEnabled]);

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
      return;
    }
  }, [dispatch, fetchError]);

  return { loadingState: loadingState, availableChannelTypes, errors };
}

const getChannelTypesWithConfiguration = (channelsConfiguration, customChannels, endpoints) => {
  const uniqueEndpointTypes = uniq(
    map(endpoints, endpoint =>
      endpoint.type === EndpointTypes.CUSTOM_CHANNEL
        ? `${EndpointTypes.CUSTOM_CHANNEL}:${endpoint.configuration?.customChannelId}`
        : EndpointTypes[endpoint.type]
    )
  );

  return map(uniqueEndpointTypes, channelId => {
    const defaultConfig = has(channelsConfiguration, channelId) ? channelsConfiguration[channelId] : {};

    const isCustomChannel = channelId.startsWith(EndpointTypes.CUSTOM_CHANNEL);
    let customChannel;

    if (isCustomChannel) {
      const typeInfo = channelId.split(':');
      customChannel = find(customChannels, { id: typeInfo[1] });
    }

    const name = isCustomChannel ? customChannel?.name : EndpointsSections[channelId].displayName;
    const nameInDescription = isCustomChannel ? customChannel?.name : EndpointsSections[channelId].nameInDescription;

    return {
      ...defaultConfig,
      id: channelId,
      name,
      nameInDescription,
    };
  });
};

function getActiveEndpoints(endpoints, customChannels, isCustomChannelsEnabled) {
  if (!isCustomChannelsEnabled) {
    return endpoints.filter(endpoint => endpoint.type !== EndpointTypes.CUSTOM_CHANNEL);
  }

  return endpoints.filter(
    endpoint =>
      !(
        endpoint.type === EndpointTypes.CUSTOM_CHANNEL &&
        isCustomChannelArchived(customChannels, endpoint.configuration.customChannelId)
      )
  );
}

function isCustomChannelArchived(customChannels, customChannelId) {
  const customChannel = find(customChannels, { id: customChannelId });
  return customChannel?.archived ?? false;
}
